import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {
    Box,
    Button,
    Chip,
    DialogActions,
    DialogContent,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
    Dialog as RawDialog,
} from '@mui/material'
import RateCalendarGrid from 'components/Page/Rates/RateCalendarGrid'
import PageLoader from 'components/Display/Loader/PageLoader'
import {useGetAccountsQuery, useGetCurrentUserQuery, useGetRatesQuery} from 'store/Store'
import {useTranslation} from 'react-i18next'
import {TYPE_DAY} from 'constants/enum/rateType'
import {CONSTANT, NAME_RATE} from 'constants/enum/nameRate'
import {getDocumentId} from 'utils'
import Dialog from 'components/Display/Dialogs/Dialog'

const MONTH_PERIOD = 12
const HOUR_PERIOD = 24
const ALL_RATE_OPTION = '__all__'
const ALL_ACCOUNT_OPTION = '__all_accounts__'
const GLOBAL_FILTER_ALL = 'all'
const GLOBAL_FILTER_ONLY = 'global'
const GLOBAL_FILTER_LOCAL = 'local'
const RATE_NAME_OPTIONS = NAME_RATE.filter((name) => name !== CONSTANT)
const DEFAULT_TYPE_DAY = TYPE_DAY[0] || ''

const mod = (value, period) => {
    const result = value % period
    return result < 0 ? result + period : result
}

const getRateMonthsHours = (rate = {}) => {
    let months = []
    if (Array.isArray(rate.months) && rate.months.length > 0) {
        months = rate.months
    } else if (rate.monthStart && rate.monthEnd) {
        months = Array.from(
            {length: rate.monthEnd - rate.monthStart + 1},
            (_, idx) => rate.monthStart + idx
        )
    }
    months = Array.from(
        new Set(months.map((month) => Number(month)).filter((month) => Number.isFinite(month)))
    ).sort((a, b) => a - b)

    let hours = []
    if (Array.isArray(rate.hours) && rate.hours.length > 0) {
        hours = rate.hours
    } else if (rate.hourStart !== undefined && rate.hourEnd !== undefined) {
        hours = Array.from(
            {length: rate.hourEnd - rate.hourStart + 1},
            (_, idx) => rate.hourStart + idx
        )
    }
    hours = Array.from(
        new Set(hours.map((hour) => Number(hour)).filter((hour) => Number.isFinite(hour)))
    ).sort((a, b) => a - b)

    return {months, hours}
}

const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`

const RatesCalendarPreviewDialog = ({open, onClose}) => {
    const {t, i18n} = useTranslation()
    const {data: userToken} = useGetCurrentUserQuery()

    const [typeDayFilter, setTypeDayFilter] = useState(DEFAULT_TYPE_DAY)
    const [selectedRateNames, setSelectedRateNames] = useState(RATE_NAME_OPTIONS)
    const [accountFilter, setAccountFilter] = useState(ALL_ACCOUNT_OPTION)
    const [isGlobalFilter, setIsGlobalFilter] = useState(GLOBAL_FILTER_ALL)
    const [highlightedLockedCells, setHighlightedLockedCells] = useState(new Set())
    const [hoveredLockedCells, setHoveredLockedCells] = useState(new Set())
    const [lockedRate, setLockedRate] = useState(null)

    const superUserWithoutAccount = useMemo(() => {
        const accountId = getDocumentId(userToken?.account)
        return !accountId && Boolean(userToken?.isSuperUser)
    }, [userToken])

    useEffect(() => {
        if (!open) {
            setTypeDayFilter(DEFAULT_TYPE_DAY)
            setSelectedRateNames(RATE_NAME_OPTIONS)
            setAccountFilter(ALL_ACCOUNT_OPTION)
            setIsGlobalFilter(GLOBAL_FILTER_ALL)
            setHighlightedLockedCells(new Set())
            setHoveredLockedCells(new Set())
            setLockedRate(null)
        }
    }, [open])

    const {data: accountsResponse, isLoading: isAccountsLoading} = useGetAccountsQuery(
        {},
        {skip: !(superUserWithoutAccount && open)}
    )

    const accountOptions = useMemo(() => accountsResponse?.data || [], [accountsResponse])

    const query = useMemo(() => {
        if (!open) return null
        const accountId = getDocumentId(userToken?.account)
        const baseQuery = {status: true}
        if (accountId) {
            return {...baseQuery, account: accountId}
        }
        if (superUserWithoutAccount) {
            const params = {...baseQuery}
            if (accountFilter && accountFilter !== ALL_ACCOUNT_OPTION) {
                params.account = accountFilter
            }
            if (isGlobalFilter === GLOBAL_FILTER_ONLY) {
                params.isGlobal = true
            } else if (isGlobalFilter === GLOBAL_FILTER_LOCAL) {
                params.isGlobal = false
            }
            return params
        }
        if (userToken?.isSuperUser) {
            const params = {...baseQuery}
            if (isGlobalFilter === GLOBAL_FILTER_ONLY) {
                params.isGlobal = true
            } else if (isGlobalFilter === GLOBAL_FILTER_LOCAL) {
                params.isGlobal = false
            }
            return params
        }
        return baseQuery
    }, [open, userToken, superUserWithoutAccount, accountFilter, isGlobalFilter])

    const {data: ratesResponse, isLoading} = useGetRatesQuery(query, {skip: !query})

    const handleRateNamesChange = useCallback(
        (event) => {
            const value = event.target.value
            const list = typeof value === 'string' ? value.split(',') : value
            if (list.includes(ALL_RATE_OPTION)) {
                if (selectedRateNames.length === RATE_NAME_OPTIONS.length) {
                    setSelectedRateNames([])
                } else {
                    setSelectedRateNames(RATE_NAME_OPTIONS)
                }
                return
            }
            setSelectedRateNames(list.filter((item) => item !== ALL_RATE_OPTION))
        },
        [selectedRateNames.length]
    )

    const filteredRates = useMemo(() => {
        if (!open || !ratesResponse?.data) return []
        return ratesResponse.data.filter((rate = {}) => {
            if (!rate.status) return false
            if (rate.category !== 'electricity') return false
            if (rate.name === CONSTANT) return false
            if (typeDayFilter && rate.typeDay !== typeDayFilter) return false
            if (selectedRateNames.length && !selectedRateNames.includes(rate.name)) return false
            if (superUserWithoutAccount) {
                if (accountFilter !== ALL_ACCOUNT_OPTION) {
                    const rateAccountId = getDocumentId(rate.account)
                    if (rateAccountId !== accountFilter) return false
                }
                if (isGlobalFilter === GLOBAL_FILTER_ONLY && !rate.isGlobal) return false
                if (isGlobalFilter === GLOBAL_FILTER_LOCAL && rate.isGlobal) return false
            } else {
                if (isGlobalFilter === GLOBAL_FILTER_ONLY && !rate.isGlobal) return false
                if (isGlobalFilter === GLOBAL_FILTER_LOCAL && rate.isGlobal) return false
            }
            return true
        })
    }, [
        open,
        ratesResponse,
        selectedRateNames,
        typeDayFilter,
        superUserWithoutAccount,
        accountFilter,
        isGlobalFilter,
    ])

    const {lockedCellSet, lockedCellMap, rateCellSetMap} = useMemo(() => {
        if (!filteredRates.length)
            return {
                lockedCellSet: new Set(),
                lockedCellMap: new Map(),
                rateCellSetMap: new Map(),
            }
        const selections = new Set()
        const cellMap = new Map()
        const rateCells = new Map()
        filteredRates.forEach((rate = {}) => {
            const {months, hours} = getRateMonthsHours(rate)
            months.forEach((monthValue) => {
                const monthIndex = mod(Number(monthValue) - 1, MONTH_PERIOD)
                hours.forEach((hourValue) => {
                    const hourIndex = mod(Number(hourValue), HOUR_PERIOD)
                    if (!Number.isNaN(monthIndex) && !Number.isNaN(hourIndex)) {
                        const cellKey = `${monthIndex}:${hourIndex}`
                        selections.add(cellKey)
                        if (!cellMap.has(cellKey)) {
                            cellMap.set(cellKey, rate)
                        }
                        const rateId = getDocumentId(rate)
                        if (rateId) {
                            if (!rateCells.has(rateId)) {
                                rateCells.set(rateId, new Set())
                            }
                            rateCells.get(rateId).add(cellKey)
                        }
                    }
                })
            })
        })
        return {lockedCellSet: selections, lockedCellMap: cellMap, rateCellSetMap: rateCells}
    }, [filteredRates])

    useEffect(() => {
        setHighlightedLockedCells(new Set())
        setHoveredLockedCells(new Set())
        setLockedRate(null)
    }, [typeDayFilter, selectedRateNames, accountFilter, isGlobalFilter])

    const getRateCellSet = useCallback(
        (rate) => {
            if (!rate) return new Set()
            const rateId = getDocumentId(rate)
            if (rateId && rateCellSetMap.has(rateId)) {
                return new Set(rateCellSetMap.get(rateId))
            }
            const {months, hours} = getRateMonthsHours(rate)
            const cells = new Set()
            months.forEach((monthValue) => {
                const monthIndex = mod(Number(monthValue) - 1, MONTH_PERIOD)
                hours.forEach((hourValue) => {
                    const hourIndex = mod(Number(hourValue), HOUR_PERIOD)
                    if (!Number.isNaN(monthIndex) && !Number.isNaN(hourIndex)) {
                        cells.add(`${monthIndex}:${hourIndex}`)
                    }
                })
            })
            return cells
        },
        [rateCellSetMap]
    )

    const handleLockedCellHover = useCallback(
        (cellKey) => {
            const rate = lockedCellMap.get(cellKey)
            if (!rate) {
                setHoveredLockedCells(new Set())
                return
            }
            setHoveredLockedCells(getRateCellSet(rate))
        },
        [getRateCellSet, lockedCellMap]
    )

    const handleLockedCellLeave = useCallback(() => {
        setHoveredLockedCells(new Set())
    }, [])

    const handleLockedCellClick = useCallback(
        (cellKey) => {
            const rate = lockedCellMap.get(cellKey)
            if (rate) {
                setLockedRate(rate)
                setHighlightedLockedCells(getRateCellSet(rate))
            }
        },
        [getRateCellSet, lockedCellMap]
    )

    const closeLockedRateDialog = useCallback(() => {
        setLockedRate(null)
        setHighlightedLockedCells(new Set())
        setHoveredLockedCells(new Set())
    }, [])

    const monthLabels = useMemo(
        () =>
            Array.from({length: MONTH_PERIOD}, (_, index) =>
                new Date(2000, index, 1).toLocaleString(i18n.language, {month: 'long'})
            ),
        [i18n.language]
    )

    const lockedRateMonthsHours = useMemo(() => {
        if (!lockedRate) return {months: [], hours: []}
        return getRateMonthsHours(lockedRate)
    }, [lockedRate])

    const displayedSelectedCells = useMemo(() => {
        const combined = new Set()
        highlightedLockedCells.forEach((cell) => combined.add(cell))
        hoveredLockedCells.forEach((cell) => combined.add(cell))
        return combined
    }, [highlightedLockedCells, hoveredLockedCells])

    const hasNoRates = !isLoading && (!filteredRates.length || !lockedCellSet.size)

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth withFormAction={false}>
                <DialogContent>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: {xs: 'column', md: 'row'},
                            gap: 3,
                        }}>
                        <Paper
                            elevation={1}
                            sx={{
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                flex: {xs: 'none', md: '0 0 340px'},
                                minWidth: {md: 320},
                            }}>
                            <Typography variant="subtitle1">
                                {t('rates.calendar.preview.filtersTitle', {
                                    defaultValue: 'Preview filters',
                                })}
                            </Typography>
                            <Stack spacing={2}>
                                {superUserWithoutAccount && (
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        disabled={isAccountsLoading}>
                                        <InputLabel id="preview-account-label">
                                            {t('rates.calendar.preview.accountLabel', {
                                                defaultValue: 'Account',
                                            })}
                                        </InputLabel>
                                        <Select
                                            labelId="preview-account-label"
                                            value={accountFilter}
                                            label={t('rates.calendar.preview.accountLabel', {
                                                defaultValue: 'Account',
                                            })}
                                            onChange={(event) =>
                                                setAccountFilter(event.target.value)
                                            }
                                            renderValue={(selected) => {
                                                if (selected === ALL_ACCOUNT_OPTION) {
                                                    return t('rates.calendar.preview.allAccounts', {
                                                        defaultValue: 'All accounts',
                                                    })
                                                }
                                                const accountName = accountOptions.find(
                                                    (account) => getDocumentId(account) === selected
                                                )?.name
                                                return accountName || selected
                                            }}>
                                            <MenuItem value={ALL_ACCOUNT_OPTION}>
                                                {t('rates.calendar.preview.allAccounts', {
                                                    defaultValue: 'All accounts',
                                                })}
                                            </MenuItem>
                                            {accountOptions.map((account) => {
                                                const accountId = getDocumentId(account)
                                                if (!accountId) return null
                                                return (
                                                    <MenuItem key={accountId} value={accountId}>
                                                        {account?.name || accountId}
                                                    </MenuItem>
                                                )
                                            })}
                                        </Select>
                                    </FormControl>
                                )}

                                <FormControl fullWidth size="small">
                                    <InputLabel id="preview-type-day-label">
                                        {t('rates.calendar.preview.typeDayLabel', {
                                            defaultValue: 'Type day',
                                        })}
                                    </InputLabel>
                                    <Select
                                        labelId="preview-type-day-label"
                                        value={typeDayFilter}
                                        label={t('rates.calendar.preview.typeDayLabel', {
                                            defaultValue: 'Type day',
                                        })}
                                        onChange={(event) => setTypeDayFilter(event.target.value)}>
                                        {TYPE_DAY.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {t(`selectOptions.${option}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {superUserWithoutAccount && (
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="preview-is-global-label">
                                            {t('rates.calendar.preview.isGlobalLabel', {
                                                defaultValue: 'Scope',
                                            })}
                                        </InputLabel>
                                        <Select
                                            labelId="preview-is-global-label"
                                            value={isGlobalFilter}
                                            label={t('rates.calendar.preview.isGlobalLabel', {
                                                defaultValue: 'Scope',
                                            })}
                                            onChange={(event) =>
                                                setIsGlobalFilter(event.target.value)
                                            }>
                                            <MenuItem value={GLOBAL_FILTER_ALL}>
                                                {t('rates.calendar.preview.isGlobalAll', {
                                                    defaultValue: 'All rates',
                                                })}
                                            </MenuItem>
                                            <MenuItem value={GLOBAL_FILTER_ONLY}>
                                                {t('rates.calendar.preview.isGlobalOnly', {
                                                    defaultValue: 'Global rates only',
                                                })}
                                            </MenuItem>
                                            <MenuItem value={GLOBAL_FILTER_LOCAL}>
                                                {t('rates.calendar.preview.isGlobalLocal', {
                                                    defaultValue: 'Account rates only',
                                                })}
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                )}

                                <FormControl fullWidth size="small">
                                    <InputLabel id="preview-rate-name-label">
                                        {t('rates.calendar.preview.rateNameLabel', {
                                            defaultValue: 'Rate name',
                                        })}
                                    </InputLabel>
                                    <Select
                                        multiple
                                        labelId="preview-rate-name-label"
                                        value={selectedRateNames}
                                        onChange={handleRateNamesChange}
                                        label={t('rates.calendar.preview.rateNameLabel', {
                                            defaultValue: 'Rate name',
                                        })}
                                        renderValue={(selected) => {
                                            if (!selected.length) {
                                                return t('rates.calendar.preview.noRateSelected', {
                                                    defaultValue: 'No rate selected',
                                                })
                                            }
                                            if (selected.length === RATE_NAME_OPTIONS.length) {
                                                return t('rates.calendar.preview.allRates', {
                                                    defaultValue: 'All rate names',
                                                })
                                            }
                                            return selected
                                                .map((value) => t(`selectOptions.${value}`))
                                                .join(', ')
                                        }}>
                                        <MenuItem value={ALL_RATE_OPTION}>
                                            {selectedRateNames.length === RATE_NAME_OPTIONS.length
                                                ? t('rates.calendar.preview.clearRates', {
                                                      defaultValue: 'Clear selection',
                                                  })
                                                : t('rates.calendar.preview.selectAllRates', {
                                                      defaultValue: 'Select all',
                                                  })}
                                        </MenuItem>
                                        {RATE_NAME_OPTIONS.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {t(`selectOptions.${option}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {selectedRateNames.length > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            flexWrap: 'wrap',
                                        }}>
                                        {selectedRateNames.map((value) => (
                                            <Chip
                                                key={value}
                                                label={t(`selectOptions.${value}`)}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                )}

                                <Typography variant="caption" color="text.secondary">
                                    {t('rates.calendar.preview.resultsSummary', {
                                        defaultValue: '{{count}} matching rates',
                                        count: filteredRates.length,
                                    })}
                                </Typography>
                            </Stack>
                        </Paper>

                        <Paper
                            elevation={1}
                            sx={{
                                p: {xs: 2, md: 3},
                                flex: 1,
                                minHeight: {md: 520},
                                display: 'flex',
                            }}>
                            {isLoading && <PageLoader show />}
                            {!isLoading && hasNoRates && (
                                <Stack
                                    spacing={1.5}
                                    alignItems="center"
                                    justifyContent="center"
                                    width="100%"
                                    py={6}>
                                    <Typography variant="subtitle1">
                                        {t('rates.calendar.preview.noRatesTitle', {
                                            defaultValue: 'No rates found',
                                        })}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        textAlign="center">
                                        {t('rates.calendar.preview.noRatesDescription', {
                                            defaultValue:
                                                'Try a different combination of filters to see configured rates on the calendar.',
                                        })}
                                    </Typography>
                                </Stack>
                            )}
                            {!isLoading && !hasNoRates && (
                                <RateCalendarGrid
                                    selectedCells={displayedSelectedCells}
                                    lockedCells={lockedCellSet}
                                    hoveredLockedCells={hoveredLockedCells}
                                    lockedCellDetails={lockedCellMap}
                                    onLockedCellClick={handleLockedCellClick}
                                    onLockedCellHover={handleLockedCellHover}
                                    onLockedCellLeave={handleLockedCellLeave}
                                    isReadOnly
                                />
                            )}
                        </Paper>
                    </Box>
                </DialogContent>
                {/* <DialogActions sx={{px: 3, py: 2}}>
                    <Button onClick={onClose}>{t('modal.close')}</Button>
                </DialogActions> */}
            </Dialog>

            <RawDialog
                open={Boolean(lockedRate)}
                onClose={closeLockedRateDialog}
                fullWidth
                maxWidth="xs"
                PaperProps={{sx: {borderRadius: 3, overflow: 'hidden'}}}>
                {lockedRate && (
                    <>
                        {/* ── Header ── */}
                        <Box sx={{
                            px: 3, pt: 3, pb: 2.5,
                            background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(99,102,241,0.04) 100%)',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}>
                            <Typography sx={{fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.75}}>
                                {t('rates.calendar.preview.dialogTitle', {defaultValue: 'Rate details'})}
                            </Typography>
                            <Typography variant="h6" fontWeight={700} sx={{lineHeight: 1.3, mb: 1.25}}>
                                {lockedRate.name}
                            </Typography>
                            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center'}}>
                                <Box sx={{px: 1.5, py: 0.5, borderRadius: 2, bgcolor: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)', display: 'flex', alignItems: 'baseline', gap: 0.5}}>
                                    <Typography sx={{fontSize: '1.25rem', fontWeight: 800, color: 'primary.main', fontVariantNumeric: 'tabular-nums', lineHeight: 1}}>
                                        {lockedRate.rate != null ? lockedRate.rate : '—'}
                                    </Typography>
                                </Box>
                                {lockedRate.typeDay && (
                                    <Chip
                                        label={t(`selectOptions.${lockedRate.typeDay}`)}
                                        size="small"
                                        sx={{height: 24, fontSize: '0.72rem', fontWeight: 600, bgcolor: 'action.selected', border: '1px solid', borderColor: 'divider'}}
                                    />
                                )}
                            </Box>
                        </Box>

                        <DialogContent sx={{px: 3, py: 2.5}}>
                            <Stack spacing={2.5}>
                                {/* Months */}
                                <Box>
                                    <Typography sx={{fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary', mb: 1}}>
                                        {t('rates.calendar.lockedRate.months')}
                                    </Typography>
                                    {lockedRateMonthsHours.months.length ? (
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75}}>
                                            {lockedRateMonthsHours.months.map((month) => (
                                                <Chip
                                                    key={month}
                                                    label={monthLabels[mod(Number(month) - 1, MONTH_PERIOD)] || month}
                                                    size="small"
                                                    sx={{height: 24, fontSize: '0.72rem', fontWeight: 500}}
                                                />
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" color="text.disabled">
                                            {t('rates.calendar.lockedRate.none')}
                                        </Typography>
                                    )}
                                </Box>

                                <Divider />

                                {/* Hours */}
                                <Box>
                                    <Typography sx={{fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary', mb: 1}}>
                                        {t('rates.calendar.lockedRate.hours')}
                                    </Typography>
                                    {lockedRateMonthsHours.hours.length ? (
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                            {lockedRateMonthsHours.hours.map((hour) => (
                                                <Box key={hour} sx={{px: 1, py: 0.25, borderRadius: 1, fontSize: '0.72rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider'}}>
                                                    {formatHour(hour)}
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" color="text.disabled">
                                            {t('rates.calendar.lockedRate.none')}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        </DialogContent>

                        <DialogActions sx={{px: 3, pb: 2.5, pt: 0}}>
                            <Button
                                onClick={closeLockedRateDialog}
                                variant="contained"
                                size="small"
                                disableElevation
                                sx={{borderRadius: 2, px: 2.5, fontWeight: 600}}>
                                {t('modal.close', {defaultValue: 'Close'})}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </RawDialog>
        </>
    )
}

export default RatesCalendarPreviewDialog
